import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { api } from '../services/api/client';
import { notify } from '../lib/notifications.js';
import { identifyUxUser } from '../utils/uxTracker.js';
import { identifyFunnelUser, track } from '../lib/funnel.js';
import { setAdsUserEmail } from '../lib/gadsUserData.js';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const sessionReadRevision = useRef(0);

  // Apply an authenticated-identity transition, flushing the API client's
  // URL-keyed GET cache + in-flight map first. The cache is keyed by URL only
  // (not by user), and under cookie-based auth setToken() never sees a token
  // change to trigger its own flush — so without this an account switch would
  // serve the previous user's cached responses (dashboard analytics, Library,
  // etc.) until the TTL expired or a full reload. Clearing here, during the
  // auth action itself, runs before the next screen re-mounts and refetches.
  // True once a signed-in user has been seen in THIS tab — lets onAuthError
  // tell "your session just died" (worth a toast) apart from "this visitor
  // was never signed in" (the everyday 401 on anonymous mount, no toast).
  const sessionWasLiveRef = useRef(false);

  const applyUser = (nextUser) => {
    // A response started before this identity change must not undo it.
    sessionReadRevision.current += 1;
    api.clearCache({ reset: true, reload: Boolean(nextUser) });
    setUser(nextUser);
    setLoading(false);
    if (nextUser) sessionWasLiveRef.current = true;
    // UX telemetry: tie the anonymous session to the account (analytics only).
    try { identifyUxUser(nextUser?.id ?? null); } catch { /* fail-silent */ }
    try { identifyFunnelUser(nextUser?.id ?? null); } catch { /* fail-silent */ }
    // Google Ads enhanced conversions (GH-1130): hashed, consent-gated; null forgets it.
    try { setAdsUserEmail(nextUser?.email ?? null); } catch { /* fail-silent */ }
  };

  // Configure API client
  useEffect(() => {
    api.onAuthError = () => {
      // Incident 2026-08-28 lesson: an expired session in an open tab used to
      // fail every action SILENTLY (owner read it as "everything is broken").
      // One explicit toast per expiry, then the login redirect explains itself.
      if (sessionWasLiveRef.current) {
        sessionWasLiveRef.current = false;
        notify({
          level: 'warning',
          title: 'Session expired',
          description: 'Please sign in again to continue.',
        });
      }
      applyUser(null);
    };
  }, []);

  // Check cookie-backed session on mount. The session token is HttpOnly,
  // so localStorage is only a legacy bearer-token fallback.
  useEffect(() => {
    loadUser();
  }, []);

  // opts.silent: refresh the user object WITHOUT flipping the global `loading`
  // flag. A loud reload makes RequireAuth render the full-screen loader, which
  // UNMOUNTS the authed subtree (incl. the billing gate) and remounts it fresh —
  // fine normally, but after a hosted-checkout return the remounted gate re-reads
  // ?checkout=success from the URL and re-runs the activation poll → reloadUser →
  // remount → infinite "Checking your session…". A silent post-pay reload keeps
  // the gate mounted so it settles on `allow` once.
  async function loadUser(opts) {
    const silent = opts?.silent === true;
    const revision = ++sessionReadRevision.current;
    // Signup/MFA has established a new cookie; old responses already belong
    // to a different identity even while its fresh /me read is still pending.
    if (opts?.fresh) api.clearCache({ reset: true, reload: false });
    try {
      if (!silent) setLoading(true);
      const userData = await api.auth.getCurrentUser();
      if (revision !== sessionReadRevision.current) return;
      if (opts?.fresh) {
        applyUser(userData);
        return;
      }
      setUser(userData);
      try { identifyUxUser(userData?.id ?? null); } catch { /* fail-silent */ }
      try { setAdsUserEmail(userData?.email ?? null); } catch { /* fail-silent */ }
    } catch (err) {
      if (revision !== sessionReadRevision.current) return;
      console.error('Failed to load user:', err);
      // A silent background refresh must never log the user out on a transient
      // error — they just paid and are active; only the loud initial/login path
      // clears the session.
      if (!silent) {
        api.setToken(null);
        applyUser(null);
      }
    } finally {
      if (revision === sessionReadRevision.current) setLoading(false);
    }
  }

  // A successful login/MFA changes the cookie before applyUser runs. Its /me
  // read must not reuse the previous session's cached or in-flight response,
  // and old 401s must not expire this new identity while /me is pending.
  function readNewSessionUser() {
    sessionReadRevision.current += 1;
    api.clearCache({ reset: true, reload: false });
    return api.auth.getCurrentUser();
  }

  async function login(email, password) {
    try {
      setError(null);
      const result = await api.auth.login(email, password);
      if (result?.requiresMfa || result?.requiresMfaSetup) {
        return result;
      }
      // Login returns a truncated user payload (no isAdmin / bypassOnboarding).
      // Refresh from /me so the user object carries the same fields the rest
      // of the app reads (review-bypass routing depends on this).
      const full = await readNewSessionUser().catch(() => result);
      applyUser(full || result);
      return full || result;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }

  async function verifyMfaLogin(attemptId, code) {
    try {
      setError(null);
      await api.auth.verifyMfaLogin(attemptId, code);
      const full = await readNewSessionUser();
      applyUser(full);
      return full;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }

  async function verifyMfaBackupCode(attemptId, backupCode) {
    try {
      setError(null);
      await api.auth.verifyMfaBackupCode(attemptId, backupCode);
      const full = await readNewSessionUser();
      applyUser(full);
      return full;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }

  async function sendMfaEmailCode(attemptId) {
    try {
      setError(null);
      return await api.auth.sendMfaEmailCode(attemptId);
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }

  async function verifyMfaEmailCode(attemptId, code) {
    try {
      setError(null);
      await api.auth.verifyMfaEmailCode(attemptId, code);
      const full = await readNewSessionUser();
      applyUser(full);
      return full;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }

  async function verifyRequiredMfaSetup(attemptId, code) {
    try {
      setError(null);
      const result = await api.auth.verifyRequiredMfaSetup(attemptId, code);
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }

  async function signup(email, password, name) {
    try {
      setError(null);
      const userData = await api.auth.signup(email, password, name);
      // GH-1130: hash the email BEFORE the sign_up conversion so gtag can attach
      // it (consent-gated inside; never rejects).
      try { await setAdsUserEmail(email); } catch { /* fail-silent */ }
      // Funnel (TASK-122): the account now exists server-side — this is the
      // top of the onboarding funnel EPIC-019 is trying to diagnose. Fires
      // whether or not MFA setup is required next; identifyFunnelUser below
      // (via applyUser) only runs once the session is actually established.
      try { track('signup_completed', { requiresMfaSetup: Boolean(userData?.requiresMfaSetup) }); } catch { /* fail-silent */ }
      if (userData?.requiresMfaSetup) {
        return userData;
      }
      applyUser(userData);
      return userData;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }

  async function logout() {
    try {
      await api.auth.logout();
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      applyUser(null);
    }
  }

  const value = {
    user,
    loading,
    error,
    login,
    verifyMfaLogin,
    verifyMfaBackupCode,
    sendMfaEmailCode,
    verifyMfaEmailCode,
    verifyRequiredMfaSetup,
    signup,
    logout,
    reloadUser: loadUser,
    isAuthenticated: !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
